import React, { useState, useEffect, useCallback } from 'react';
import { Users, Search, Eye, Trash2, CheckCircle, XCircle, X, FileText, ExternalLink, AlertTriangle, Gauge, Clock } from 'lucide-react';
import StatusBadge from '../common/StatusBadge';
import EmptyState from '../common/EmptyState';
import { AdminDriverAPI, AdminCompanyAPI, APIUtils } from '../../services/api';
import { removalSummary } from '../../utils/removalSummary';

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const ExpiryBadge = ({ label, dateStr }) => {
  const days = daysUntil(dateStr);
  if (days == null) return <p className="text-sm text-gray-400">—</p>;
  const expired = days < 0;
  const urgent = days <= 30;
  const color = expired ? 'text-red-600 bg-red-50' : urgent ? 'text-amber-600 bg-amber-50' : 'text-gray-700 bg-gray-50';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {(expired || urgent) && <AlertTriangle size={11} />}
      {new Date(dateStr).toLocaleDateString()}
      {expired ? ' (Expired)' : urgent ? ` (${days}d)` : ''}
    </span>
  );
};

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');

const resolveFileUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('/uploads')) return `${API_BASE}${path}`;
  if (path.startsWith('http')) return path;
  return path;
};

const DriverList = () => {
  const [drivers, setDrivers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('');
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    AdminCompanyAPI.getAll({ limit: 200, status: 'approved' }).then(res => {
      if (res.data.success) setCompanies(res.data.data.companies);
    }).catch(() => {});
  }, []);

  const fetchDrivers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== 'all') params.approvalStatus = statusFilter;
      if (companyFilter) params.companyId = companyFilter;
      if (search) params.search = search;

      const response = await AdminDriverAPI.getAll(params);
      if (response.data.success) {
        setDrivers(response.data.data.drivers);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Fetch drivers error:', APIUtils.handleError(error));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, companyFilter, search]);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  const handleApproval = async (driverId, approvalStatus) => {
    setActionLoading(true);
    try {
      const response = await AdminDriverAPI.updateApproval(driverId, approvalStatus, actionNotes);
      const handedBack = removalSummary(response);
      if (handedBack) alert(handedBack);
      setSelectedDriver(null);
      setActionNotes('');
      fetchDrivers();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivate = async (driverId) => {
    setActionLoading(true);
    try {
      const response = await AdminDriverAPI.deactivate(driverId, actionNotes);
      const handedBack = removalSummary(response);
      if (handedBack) alert(handedBack);
      setSelectedDriver(null);
      setActionNotes('');
      fetchDrivers();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (driverId) => {
    setActionLoading(true);
    try {
      await AdminDriverAPI.reactivate(driverId, actionNotes);
      setSelectedDriver(null);
      setActionNotes('');
      fetchDrivers();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (driverId) => {
    if (!window.confirm('Are you sure you want to remove this driver? This cannot be undone.')) return;
    setActionLoading(true);
    try {
      const response = await AdminDriverAPI.delete(driverId, actionNotes);
      const handedBack = removalSummary(response);
      if (handedBack) alert(handedBack);
      setSelectedDriver(null);
      setActionNotes('');
      fetchDrivers();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const statusTabs = ['all', 'pending', 'approved', 'rejected'];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Drivers</h1>
        <p className="text-gray-500 mt-1">Manage driver registrations and approvals</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex space-x-1">
            {statusTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setStatusFilter(tab)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg capitalize transition-colors ${
                  statusFilter === tab ? 'bg-emerald-100 text-emerald-700' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            >
              <option value="">All Companies</option>
              {companies.map((c) => (
                <option key={c._id} value={c._id}>{c.companyName}</option>
              ))}
            </select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search drivers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        ) : drivers.length === 0 ? (
          <EmptyState title="No drivers found" message="No drivers match the current filters." icon={Users} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approval</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {drivers.map((driver) => (
                    <tr key={driver._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{driver.name} {driver.surname}</p>
                          <p className="text-sm text-gray-500">{driver.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{driver.company?.companyName || '-'}</td>
                      <td className="px-6 py-4 text-sm font-mono text-gray-700">{driver.employeeCode}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {driver.assignedVehicle ? `${driver.assignedVehicle.vehicleMake} ${driver.assignedVehicle.model}` : 'None'}
                      </td>
                      <td className="px-6 py-4"><StatusBadge status={driver.approvalStatus || 'pending'} /></td>
                      <td className="px-6 py-4"><StatusBadge status={driver.status || 'offline'} /></td>
                      <td className="px-6 py-4">
                        {/* Mobile-friendly action buttons: 44px+ tap targets
                            (Apple HIG) with visible labels. Wrap on narrow
                            screens so they stack vertically instead of
                            squishing. */}
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            onClick={() => setSelectedDriver(driver)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100"
                            title="View details"
                          >
                            <Eye size={18} />
                            <span>View</span>
                          </button>
                          {driver.approvalStatus === 'pending' && (
                            <>
                              <button
                                onClick={() => {
                                  if (window.confirm(`Approve ${driver.name} ${driver.surname}?`)) {
                                    handleApproval(driver._id, 'approved');
                                  }
                                }}
                                disabled={actionLoading}
                                className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 disabled:opacity-50"
                                title="Approve"
                              >
                                <CheckCircle size={18} />
                                <span>Approve</span>
                              </button>
                              <button
                                onClick={() => {
                                  if (window.confirm(`Reject ${driver.name} ${driver.surname}?`)) {
                                    handleApproval(driver._id, 'rejected');
                                  }
                                }}
                                disabled={actionLoading}
                                className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 disabled:opacity-50"
                                title="Reject"
                              >
                                <XCircle size={18} />
                                <span>Reject</span>
                              </button>
                            </>
                          )}
                          {driver.approvalStatus === 'approved' && driver.status !== 'inactive' && (
                            <button
                              onClick={() => {
                                if (window.confirm(`Deactivate ${driver.name} ${driver.surname}?`)) {
                                  handleDeactivate(driver._id);
                                }
                              }}
                              disabled={actionLoading}
                              className="inline-flex items-center px-3 py-2 min-h-[44px] text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 disabled:opacity-50"
                            >
                              Deactivate
                            </button>
                          )}
                          {driver.approvalStatus === 'approved' && driver.status === 'inactive' && (
                            <button
                              onClick={() => {
                                if (window.confirm(`Reactivate ${driver.name} ${driver.surname}?`)) {
                                  handleReactivate(driver._id);
                                }
                              }}
                              disabled={actionLoading}
                              className="inline-flex items-center px-3 py-2 min-h-[44px] text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 disabled:opacity-50"
                            >
                              Reactivate
                            </button>
                          )}
                          {(driver.status === 'inactive' || driver.approvalStatus === 'rejected') && (
                            <button
                              onClick={() => handleDelete(driver._id)}
                              disabled={actionLoading}
                              className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50"
                              title="Remove"
                            >
                              <Trash2 size={16} />
                              <span>Remove</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination.pages > 1 && (
              <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">Page {pagination.page} of {pagination.pages}</p>
                <div className="flex space-x-2">
                  <button onClick={() => fetchDrivers(pagination.page - 1)} disabled={pagination.page <= 1} className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50">Previous</button>
                  <button onClick={() => fetchDrivers(pagination.page + 1)} disabled={pagination.page >= pagination.pages} className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50">Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {selectedDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{selectedDriver.name} {selectedDriver.surname}</h3>
              <button onClick={() => { setSelectedDriver(null); setActionNotes(''); }} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {/* Driver Photo & License Documents */}
              <div className="flex items-start gap-4 pb-4 border-b border-gray-200">
                <div className="flex-shrink-0">
                  {selectedDriver.photo ? (
                    <img
                      src={resolveFileUrl(selectedDriver.photo)}
                      alt={`${selectedDriver.name} ${selectedDriver.surname}`}
                      className="w-20 h-20 rounded-lg object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center">
                      <span className="text-gray-400 text-lg font-medium">{selectedDriver.name?.charAt(0)}{selectedDriver.surname?.charAt(0)}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500 mb-1">License Document</p>
                  {selectedDriver.licenseDocument ? (
                    selectedDriver.licenseDocument.endsWith('.pdf') ? (
                      <a
                        href={resolveFileUrl(selectedDriver.licenseDocument)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-sm hover:bg-emerald-100"
                      >
                        <FileText size={14} /> View PDF <ExternalLink size={12} />
                      </a>
                    ) : (
                      <img
                        src={resolveFileUrl(selectedDriver.licenseDocument)}
                        alt="License"
                        className="max-w-[200px] max-h-[120px] rounded-lg border border-gray-200 object-contain cursor-pointer"
                        onClick={() => window.open(resolveFileUrl(selectedDriver.licenseDocument), '_blank')}
                      />
                    )
                  ) : (
                    <p className="text-sm text-gray-400">No license document uploaded</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-gray-500">Email</p><p className="font-medium">{selectedDriver.email}</p></div>
                <div><p className="text-sm text-gray-500">Phone</p><p className="font-medium">{selectedDriver.phoneNumber}</p></div>
                <div><p className="text-sm text-gray-500">Company</p><p className="font-medium">{selectedDriver.company?.companyName}</p></div>
                <div><p className="text-sm text-gray-500">Employee Code</p><p className="font-medium font-mono">{selectedDriver.employeeCode}</p></div>
                <div><p className="text-sm text-gray-500">Approval</p><StatusBadge status={selectedDriver.approvalStatus || 'pending'} /></div>
                <div><p className="text-sm text-gray-500">Status</p><StatusBadge status={selectedDriver.status || 'offline'} /></div>
                <div><p className="text-sm text-gray-500">Registered</p><p className="font-medium">{new Date(selectedDriver.createdAt).toLocaleDateString()}</p></div>
              </div>

              {/* Telematics */}
              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5"><Gauge size={14} /> Telematics</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-0.5">Total km driven</p>
                    <p className="text-lg font-bold text-gray-900">{selectedDriver.totalKmDriven != null ? `${selectedDriver.totalKmDriven.toFixed(1)} km` : '—'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-0.5">Total trips</p>
                    <p className="text-lg font-bold text-gray-900">{selectedDriver.totalTrips || 0}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-0.5">Speeding incidents</p>
                    <p className={`text-lg font-bold ${(selectedDriver.metrics?.speedingIncidents || 0) > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                      {selectedDriver.metrics?.speedingIncidents || 0}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-0.5">Today online</p>
                    <p className="text-lg font-bold text-gray-900">
                      {(() => {
                        const today = new Date().toISOString().slice(0, 10);
                        const day = selectedDriver.dailyStats?.find(d => d.date === today);
                        if (!day) return '—';
                        const h = Math.floor((day.onlineMinutes || 0) / 60);
                        const m = (day.onlineMinutes || 0) % 60;
                        return h > 0 ? `${h}h ${m}m` : `${m}m`;
                      })()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Compliance / Expiry */}
              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5"><Clock size={14} /> Compliance</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">License expiry</p>
                    <ExpiryBadge label="License" dateStr={selectedDriver.licenseExpiry} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Insurance expiry</p>
                    <ExpiryBadge label="Insurance" dateStr={selectedDriver.insuranceExpiry} />
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none" rows={2} placeholder="Add notes..." />
                <div className="flex space-x-3 mt-3">
                  {selectedDriver.approvalStatus === 'pending' && (
                    <>
                      <button onClick={() => handleApproval(selectedDriver._id, 'approved')} disabled={actionLoading} className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 text-sm">Approve</button>
                      <button onClick={() => handleApproval(selectedDriver._id, 'rejected')} disabled={actionLoading} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 text-sm">Reject</button>
                    </>
                  )}
                  {selectedDriver.approvalStatus === 'approved' && selectedDriver.status !== 'inactive' && (
                    <button onClick={() => handleDeactivate(selectedDriver._id)} disabled={actionLoading} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 text-sm">Deactivate</button>
                  )}
                  {selectedDriver.approvalStatus === 'approved' && selectedDriver.status === 'inactive' && (
                    <button onClick={() => handleReactivate(selectedDriver._id)} disabled={actionLoading} className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 text-sm">Reactivate</button>
                  )}
                  {(selectedDriver.status === 'inactive' || selectedDriver.approvalStatus === 'rejected') && (
                    <button onClick={() => handleDelete(selectedDriver._id)} disabled={actionLoading} className="flex-1 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 font-medium disabled:opacity-50 text-sm">Remove</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverList;
